/**
 * @graph-stack product-innovation-lab
 * @graph-domain Operations
 * @graph-business ローテーション queue の並び替え UI。MultiPicker で選んだメンバーを順番付きのチップ列で表示し、ドラッグ&ドロップ（HTML5 DnD）で順序を入れ替える。先頭が当番の起点になる
 * @graph-connects none
 */
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

/** ReorderableChips の props。 */
export interface ReorderableChipsProps {
  /** 並び（先頭がローテーションの起点）。 */
  items: string[];
  /** 並べ替え後のハンドラ。 */
  onChange: (items: string[]) => void;
}

/** ドラッグで順序を入れ替えられるチップ列。2 件未満は非表示。 @graph-connects none */
export function ReorderableChips({ items, onChange }: ReorderableChipsProps): ReactNode {
  const { t } = useTranslation();
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  if (items.length < 2) return null;

  const moveTo = (target: number): void => {
    if (dragIndex === null || dragIndex === target) return;
    const next = [...items];
    const moved = next.splice(dragIndex, 1)[0];
    if (moved === undefined) return;
    next.splice(target, 0, moved);
    setDragIndex(null);
    onChange(next);
  };

  return (
    <div className="space-y-1">
      <span className="text-xs text-[var(--color-text-muted)]">
        {t('ops.reorderableChips.orderHint')}
      </span>
      <div className="flex flex-wrap gap-1.5">
        {items.map((name, i) => (
          <span
            key={name}
            draggable
            onDragStart={() => {
              setDragIndex(i);
            }}
            onDragOver={(e) => {
              e.preventDefault();
            }}
            onDrop={() => {
              moveTo(i);
            }}
            onDragEnd={() => {
              setDragIndex(null);
            }}
            className={`inline-flex cursor-grab items-center gap-1 rounded-full border px-2.5 py-1 text-xs active:cursor-grabbing ${
              dragIndex === i
                ? 'border-[var(--color-accent-primary)] bg-[var(--color-bg-hover)] text-[var(--color-text-primary)]'
                : 'border-[var(--color-border)] bg-[var(--color-bg-elevated)] text-[var(--color-text-secondary)]'
            }`}
          >
            <span aria-hidden="true" className="text-[var(--color-text-muted)]">
              ⠿
            </span>
            <span className="text-[var(--color-text-muted)]">{i + 1}.</span> {name}
          </span>
        ))}
      </div>
    </div>
  );
}
